import axios, { AxiosResponse } from 'axios';
// types
import {
  ApiPageStatus,
  MessCode,
  TypeRp
} from '../types/enums';
import {
  ApiResponse,
  ClsDangNhap,
  KtToken,
  KtTokenAPIResponse,
} from '../types/api-response';
// constants
import {
  API_CONFIG,
  STORAGE_KEYS,
  SECURITY_HEADERS
} from '../constants/api';
// utils
import {
  isApiSuccess,
  getErrorMessage,
  createApiDate,
  createHashCode
} from '../utils/api-helpers';

// ----------------------------------------------------------------------

// Create axios instance
const authApi = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  timeout: API_CONFIG.TIMEOUT,
  headers: API_CONFIG.HEADERS,
});

// Request interceptor to add auth token
authApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(STORAGE_KEYS.AUTHENTICATE_TOKEN);
    if (token) {
      config.headers[SECURITY_HEADERS.AUTHENTICATE_TOKEN] = token;
    }

    const funcsTagActive = localStorage.getItem(STORAGE_KEYS.PERMISSIONS);
    if (funcsTagActive) {
      config.headers[SECURITY_HEADERS.FUNCS_TAG_ACTIVE] = funcsTagActive;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

authApi.interceptors.response.use(
  (response) => response,
  (error) => Promise.reject(error)
);

// ----------------------------------------------------------------------

// Register request type
export interface RegisterRequest {
  tenTaiKhoan: string;
  matKhau: string;
  ngayKhoiTao: string;
  trangThai: number;
  sdt: string;
  email: string;
}

// Authentication API service
export const authApiService = {
  // Login user
  async login(username: string, password: string): Promise<ApiResponse<KtToken>> {
    try {
      // Create API date and hash code for security
      const apiDate = createApiDate();
      const hashCode = await createHashCode(apiDate, username, password);
      // Prepare login request
      const loginRequest: ClsDangNhap = {
        uname: username,
        pass: password,
        apiDate,
        hashCode
      };
      console.log(`${API_CONFIG.BASE_URL}/Home/DangNhap`);
      const response: AxiosResponse<ApiResponse<KtToken>> = await authApi.post(
        `${API_CONFIG.BASE_URL}/Home/DangNhap`,
        loginRequest
      );

      // Check if login was successful
      if (isApiSuccess(response.data)) {
        const tokenData = response.data.oneItem;

        if (tokenData) {
          // Store tokens in session storage
          if (tokenData.authenticateToken) {
            localStorage.setItem(STORAGE_KEYS.AUTHENTICATE_TOKEN, tokenData.authenticateToken);
          }
          if (tokenData.refreshToken) {
            localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, tokenData.refreshToken);
          }

          if (tokenData.expiredAuthenticateToken) {
            localStorage.setItem(STORAGE_KEYS.EXPIRED_AUTHENTICATE_TOKEN, tokenData.expiredAuthenticateToken);
          }

          if (tokenData.expiredRefreshToken) {
            localStorage.setItem(STORAGE_KEYS.EXPIRED_REFRESH_TOKEN, tokenData.expiredRefreshToken);
          }

          if (tokenData.service) {
            localStorage.setItem(STORAGE_KEYS.SERVICE_URL, tokenData.service);
          }
          if (tokenData.htMenuModels) {
            localStorage.setItem(STORAGE_KEYS.MENU_ITEMS, JSON.stringify(tokenData.htMenuModels));
          }
          // Store user info 
          if (tokenData.htTaiKhoan) {
            localStorage.setItem(STORAGE_KEYS.USER_INFO, JSON.stringify(tokenData.htTaiKhoan));
          }

          // Store permissions 
          if (tokenData.funcsTagActives) {
            localStorage.setItem(STORAGE_KEYS.PERMISSIONS, JSON.stringify(tokenData.funcsTagActives));
          }
        }
      }

      return response.data;
    } catch (error: any) {
      console.error('Login error:', error);
      // Handle API error response
      if (error.response?.data) {
        const apiResponse = error.response.data;
        const errorMessage = getErrorMessage(apiResponse.MessCode);
        throw new Error(apiResponse.Message || errorMessage);
      }

      // Handle network/other errors
      if (error.code === 'ECONNABORTED') {
        throw new Error('Hết thời gian chờ. Vui lòng thử lại.');
      }

      if (error.message === 'Network Error') {
        throw new Error('Máy chủ tạm thời không hoạt động');
      }

      throw new Error(error.message || 'Đăng nhập thất bại. Vui lòng thử lại.');
    }
  },

  // Refresh token
  async refreshToken(): Promise<KtTokenAPIResponse> {
    try {
      const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);

      if (!refreshToken) {
        throw new Error('Không tìm thấy refresh token');
      }

      // GIỮ NGUYÊN CODE CỦA BẠN DƯỚI DẠNG GET/KHÔNG BODY/QUERY
      const response: AxiosResponse<KtTokenAPIResponse> = await authApi.get(
        `${API_CONFIG.BASE_URL}/HeThong/RefreshToken`,
      );

      if (isApiSuccess(response.data) && response.data.oneItem) {
        const tokenData = response.data.oneItem;

        // Update stored tokens
        if (tokenData.authenticateToken) {
          localStorage.setItem(STORAGE_KEYS.AUTHENTICATE_TOKEN, tokenData.authenticateToken);
        }

        if (tokenData.expiredAuthenticateToken) {
          localStorage.setItem(STORAGE_KEYS.EXPIRED_AUTHENTICATE_TOKEN, tokenData.expiredAuthenticateToken);
        }

        // Update menu data if available
        if (tokenData.htMenuModels) {
          localStorage.setItem(STORAGE_KEYS.MENU_ITEMS, JSON.stringify(tokenData.htMenuModels));
        }

        console.log('Token refreshed successfully');
      }

      return response.data;
    } catch (error: any) {
      console.error('Refresh token error:', error);
      // ❌ SỬA LỖI: Không clear tokens ở đây. Để Interceptor chính xử lý khi refresh thất bại.
      // clearAuthTokens(); 
      throw error;
    }
  },

  async getCurrentUser(): Promise<ApiResponse<KtToken>> {
    try {
      const userInfo = localStorage.getItem(STORAGE_KEYS.USER_INFO);
      const permissions = localStorage.getItem(STORAGE_KEYS.PERMISSIONS);
      const authenticateToken = localStorage.getItem(STORAGE_KEYS.AUTHENTICATE_TOKEN);
      const expiredAuthenticateToken = localStorage.getItem(STORAGE_KEYS.EXPIRED_AUTHENTICATE_TOKEN);
      const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
      const expiredRefreshToken = localStorage.getItem(STORAGE_KEYS.EXPIRED_REFRESH_TOKEN);
      const service = localStorage.getItem(STORAGE_KEYS.SERVICE_URL);
      const htMenuModels = localStorage.getItem(STORAGE_KEYS.MENU_ITEMS);

      if (userInfo && permissions) {
        const tokenData: KtToken = {
          messCode: MessCode.IsOK,
          message: 'OK',
          authenticateToken,
          expiredAuthenticateToken,
          refreshToken,
          expiredRefreshToken,
          service,
          htTaiKhoan: JSON.parse(userInfo),
          funcsTagActives: JSON.parse(permissions),
          htMenuModels: htMenuModels ? JSON.parse(htMenuModels) : [],
        };

        const response: ApiResponse<KtToken> = {
          messCode: MessCode.IsOK,
          message: 'OK',
          typeRp: TypeRp.ItemObj,
          oneItem: tokenData,
          items: [],
          messageGoiY: '',
          dataPage: {
            tableName: null,
            rowCount: 1,
            rowCountNoWhere: 1,
            skip: 0,
            top: 1,
            orderby: null,
            status: ApiPageStatus.DangDung,
            searchby: null,
            datefm: 'dmy',
          },
          lstOptionModelType: [],
        };

        return response;
      }

      throw new Error('Không tìm thấy thông tin người dùng');
    } catch (error: any) {
      console.error('Get current user error:', error);
      throw new Error('Không thể lấy thông tin người dùng');
    }
  },


  // Logout user
  async logout(): Promise<void> {
    try {
      console.log('Đăng xuất thành công');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Always clear session
      clearAuthTokens();
    }
  },

  // Register new user
  async register(registerData: RegisterRequest): Promise<ApiResponse<any>> {
    try {
      // Create API date and hash code for security

      // Prepare register request with additional security fields
      const requestData = {
        ...registerData,
      };

      console.log('Register request:', {
        requestData
      });

      const response: AxiosResponse<ApiResponse<any>> = await authApi.post(
        `${API_CONFIG.BASE_URL}/Home/DangKy`, // Adjust endpoint as needed
        requestData
      );

      console.log('Register response:', response.data);

      return response.data;
    } catch (error: any) {
      console.error('Register error:', error);

      // Handle API error response
      if (error.response?.data)
        return error.response.data;

      // Handle network errors
      if (error.code === 'ECONNABORTED')
        throw new Error('Hết thời gian chờ. Vui lòng thử lại.');

      if (error.message === 'Network Error')
        throw new Error('Lỗi kết nối mạng. Vui lòng kiểm tra kết nối internet.');

      throw new Error(error.message || 'Đăng ký thất bại. Vui lòng thử lại.');
    }
  },

  // Verify token validity
  async verifyToken(): Promise<boolean> {
    try {
      const token = localStorage.getItem(STORAGE_KEYS.AUTHENTICATE_TOKEN);
      const expiredDate = localStorage.getItem(STORAGE_KEYS.EXPIRED_AUTHENTICATE_TOKEN);

      if (!token || !expiredDate) {
        return false;
      }

      // Check if token is expired
      const expirationTime = new Date(expiredDate).getTime();
      const currentTime = new Date().getTime();

      if (currentTime >= expirationTime) {
        console.warn('Token đã hết hạn');
        clearAuthTokens();
        return false;
      }

      return true;
    } catch (error) {
      console.error('Verify token error:', error);
      return false;
    }
  },
};

// ----------------------------------------------------------------------

// Utility functions for token management
export function clearAuthTokens(): void {
  localStorage.removeItem(STORAGE_KEYS.AUTHENTICATE_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.EXPIRED_AUTHENTICATE_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.EXPIRED_REFRESH_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.SERVICE_URL);
  localStorage.removeItem(STORAGE_KEYS.USER_INFO);
  localStorage.removeItem(STORAGE_KEYS.PERMISSIONS);
  localStorage.removeItem(STORAGE_KEYS.MENU_ITEMS);
}

export function getStoredTokens(): {
  authenticateToken: string;
  expiredAuthenticateToken: string;
  refreshToken: string;
  expiredRefreshToken: string;
} | null {
  const authenticateToken = localStorage.getItem(STORAGE_KEYS.AUTHENTICATE_TOKEN);
  const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
  const expiredAuthenticateToken = localStorage.getItem(STORAGE_KEYS.EXPIRED_AUTHENTICATE_TOKEN);
  const expiredRefreshToken = localStorage.getItem(STORAGE_KEYS.EXPIRED_REFRESH_TOKEN);

  // Chỉ cần authenticateToken là đủ, các tokens khác là optional
  if (authenticateToken) {
    return {
      authenticateToken,
      refreshToken: refreshToken || '',
      expiredAuthenticateToken: expiredAuthenticateToken || '',
      expiredRefreshToken: expiredRefreshToken || '',
    };
  }

  return null;
}

export function isTokenExpired(expiredDate: string): boolean {
  // Nếu không có expiredDate, coi như token chưa hết hạn
  if (!expiredDate) {
    return false;
  }

  try {
    const expirationTime = new Date(expiredDate).getTime();
    const currentTime = new Date().getTime();
    return currentTime >= expirationTime;
  } catch (error) {
    return false; // Nếu không parse được date, coi như chưa hết hạn
  }
}

// Set session with token
export function setSession(authenticateToken: string | null) {
  if (authenticateToken) {
    localStorage.setItem(STORAGE_KEYS.AUTHENTICATE_TOKEN, authenticateToken);
    authApi.defaults.headers.common[SECURITY_HEADERS.AUTHENTICATE_TOKEN] = authenticateToken;
  } else {
    localStorage.removeItem(STORAGE_KEYS.AUTHENTICATE_TOKEN);
    delete authApi.defaults.headers.common[SECURITY_HEADERS.AUTHENTICATE_TOKEN];
  }
}

export default authApi;
